<script setup lang="ts">
import { computed, inject, useId } from "vue";
import { ErrorText } from "../ErrorText";
import { HelperText } from "../HelperText";
import type { RadioGroupProps } from "./RadioGroup";

interface RadioPropsBase {
  /** Disables the radio and prevents user interaction. */
  disabled?: boolean;
  /** Displays an optional helper text below the checkbox. */
  helperText?: string;
  modelValue?: string;
  /** The name attribute for the radio input. */
  name?: string;
  value: string;
}

interface RadioPropsError extends RadioPropsBase {
  /** Displays the radio in an error state. */
  error: boolean;
  /** Displays an optional error message below the radio when `error` is true. */
  errorText?: string;
}

interface RadioPropsValid extends RadioPropsBase {
  error?: boolean;
  errorText?: never;
}

export type RadioProps = RadioPropsError | RadioPropsValid;

const {
  disabled = false,
  error,
  errorText,
  helperText,
  modelValue,
  name,
  value,
} = defineProps<RadioProps>();

const radioGroupProps = inject<RadioGroupProps>("radioGroupContext");

const computedName = computed(() => name || radioGroupProps?.name);
const hasError = computed(() => error || radioGroupProps?.error);
const isChecked = computed(() => modelValue === value || radioGroupProps?.modelValue === value);
const isDisabled = computed(() => disabled || radioGroupProps?.disabled);

const errorTextId = `error-text-${useId()}`;
const helperTextId = `helper-text-${useId()}`;

// `display: contents` removes the wrapper's box but keeps its children rendered normally.
// This allows block-level elements (like divs or tables) inside a <slot> to be valid inside <label>.
// It also enables applying visual states (e.g. "disabled") to the slotted content.
</script>

<template>
  <template v-if="$slots.default">
    <div>
      <label
        class="group grid auto-cols-min grid-cols-[max-content_auto] gap-x-3 gap-y-0.5 align-baseline"
        data-testid="clr-radio-label"
      >
        <input
          :aria-describedby="
            [errorText ? errorTextId : null, helperText ? helperTextId : null]
              .filter(Boolean)
              .join(' ')
          "
          class="clr-radio m-0 grid h-5 w-5 appearance-none place-content-center rounded-full border-2 border-dark-blue before:h-3 before:w-3 before:scale-0 before:rounded-full before:shadow-[inset_1em_1em] before:shadow-dark-blue checked:before:scale-90 focus:rounded-full disabled:cursor-not-allowed disabled:border-disabled-01 disabled:before:shadow-disabled-01 before:motion-reduce:transition-transform"
          :class="{ 'radio-error border-error-01 before:shadow-error-01': hasError }"
          :checked="isChecked"
          :disabled="isDisabled"
          :name="computedName"
          type="radio"
          :value="value"
          data-testid="clr-radio-input"
          @input="radioGroupProps.modelValue = value"
        />
        <!-- The span behaves like it's not there, but still applies styles like `disabled` -->
        <span class="contents" :class="{ 'cursor-not-allowed text-disabled-01': isDisabled }">
          <slot />
        </span>
        <HelperText
          v-if="helperText"
          :id="helperTextId"
          class="col-start-2"
          :class="{ 'text-disabled-01': isDisabled }"
          data-testid="clr-radio-helper-text"
        >
          {{ helperText }}
        </HelperText>
        <ErrorText
          v-if="!disabled && error && errorText?.trim()"
          :id="errorTextId"
          class="pointer-events-none col-start-2"
          data-testid="clr-radio-error-text"
        >
          {{ errorText }}
        </ErrorText>
      </label>
    </div>
  </template>
</template>

<style lang="postcss" scoped>
.clr-radio {
  transform: translateY(0.075em);
}
</style>
